import React, { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { QUIZZES } from "../lib/quizzes";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const SCALE = [
  { v: 1, label: "Rarely" },
  { v: 2, label: "Sometimes" },
  { v: 3, label: "Often" },
  { v: 4, label: "Usually" },
  { v: 5, label: "Always" },
];

export default function Quiz() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const quiz = QUIZZES[key];

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [locked, setLocked] = useState(false);

  const result = useMemo(() => {
    if (!done || !quiz) return null;
    if (quiz.type === "category") {
      const tally = {};
      Object.values(answers).forEach((k) => { tally[k] = (tally[k] || 0) + 1; });
      const winner = Object.entries(tally).sort((a, b) => b[1] - a[1])[0]?.[0];
      return { ...quiz.results[winner], key: winner };
    }
    const sum = Object.values(answers).reduce((s, v) => s + Number(v), 0);
    const band = quiz.bands.find((b) => sum >= b.min && sum <= b.max) || quiz.bands[quiz.bands.length - 1];
    return { ...band, key: `L${band.level}`, score: sum };
  }, [done, answers, quiz]);

  if (!quiz) {
    return <Layout><div className="section-pad text-center text-brand-muted">Quiz not found. <Link to="/zenergize-holistic-health-club" className="text-brand-sage underline">Back</Link></div></Layout>;
  }

  const total = quiz.questions.length;
  const progress = done ? 100 : Math.round((step / total) * 100);

  const answer = (val) => {
    if (locked) return;
    setLocked(true);
    const next = { ...answers, [step]: val };
    setAnswers(next);
    if (step + 1 < total) {
      setTimeout(() => { setStep(step + 1); setLocked(false); }, 220);
    } else {
      setTimeout(() => finish(next), 220);
    }
  };

  const finish = async (finalAnswers) => {
    setAnswers(finalAnswers);
    setDone(true);
    // compute result label for saving
    let label = "";
    if (quiz.type === "category") {
      const tally = {};
      Object.values(finalAnswers).forEach((k) => { tally[k] = (tally[k] || 0) + 1; });
      const winner = Object.entries(tally).sort((a, b) => b[1] - a[1])[0]?.[0];
      label = quiz.results[winner]?.title || winner;
    } else {
      const sum = Object.values(finalAnswers).reduce((s, v) => s + Number(v), 0);
      const band = quiz.bands.find((b) => sum >= b.min && sum <= b.max) || quiz.bands[quiz.bands.length - 1];
      label = band.title;
    }
    if (user) {
      try {
        await api.post("/assessments", { quiz_key: quiz.key, quiz_label: quiz.title, result: label });
        toast.success("Result saved to your dashboard.");
      } catch { /* ignore */ }
    }
  };

  const restart = () => { setStep(0); setAnswers({}); setDone(false); setLocked(false); };
  const q = quiz.questions[step];

  return (
    <Layout>
      <div className="section-pad max-w-2xl mx-auto">
        <Link to="/zenergize-holistic-health-club" className="inline-flex items-center gap-2 text-brand-sage text-sm mb-6"><ArrowLeft size={16} /> The Club</Link>

        <div className="text-center">
          <span className="pill bg-brand-cream2 text-brand-tan">Free Assessment</span>
          <h1 className="font-display text-4xl font-semibold text-brand-ink mt-3">{quiz.title}</h1>
          <p className="text-brand-inksoft mt-2">{quiz.subtitle}</p>
        </div>

        {/* Progress */}
        <div className="mt-8 h-2 rounded-full bg-brand-cream2 overflow-hidden">
          <motion.div className="h-full" style={{ background: quiz.accent }} animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>
        {!done && <p className="text-xs text-brand-muted mt-2 text-center">Question {step + 1} of {total}</p>}

        <div className="mt-6 min-h-[340px]">
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }}
                className="card-surface p-7" data-testid="quiz-question">
                <h3 className="font-display text-2xl text-brand-ink">{quiz.type === "score" ? q : q.q}</h3>
                <div className="mt-6 space-y-3">
                  {quiz.type === "category" ? (
                    q.options.map((opt, i) => (
                      <button key={i} onClick={() => answer(opt.key)} data-testid={`quiz-option-${i}`}
                        className="w-full text-left rounded-xl border border-brand-line px-5 py-4 text-brand-ink hover:border-brand-sage hover:bg-brand-cream transition-colors">
                        {opt.label}
                      </button>
                    ))
                  ) : (
                    <div className="grid grid-cols-5 gap-2">
                      {SCALE.map((s) => (
                        <button key={s.v} onClick={() => answer(s.v)} data-testid={`quiz-scale-${s.v}`}
                          className="rounded-xl border border-brand-line py-4 flex flex-col items-center gap-1 hover:border-brand-sage hover:bg-brand-cream transition-colors">
                          <span className="font-display text-2xl text-brand-ink">{s.v}</span>
                          <span className="text-[10px] text-brand-muted">{s.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {step > 0 && (
                  <button onClick={() => setStep(step - 1)} className="mt-5 text-sm text-brand-muted hover:text-brand-ink inline-flex items-center gap-1"><ArrowLeft size={14} /> Back</button>
                )}
              </motion.div>
            ) : (
              <motion.div key="result" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="card-surface p-8 text-center" data-testid="quiz-result">
                <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center text-white" style={{ background: result.color || quiz.accent }}>
                  <Sparkles size={26} />
                </div>
                <p className="text-brand-muted text-sm mt-4">Your result</p>
                <h2 className="font-display text-3xl font-semibold text-brand-ink mt-1">{result.title}</h2>
                {result.score != null && <p className="text-brand-gold text-sm mt-1">Vitality score: {result.score} / 35</p>}
                <p className="mt-4 text-brand-inksoft leading-relaxed">{result.blurb}</p>

                {user ? (
                  <p className="mt-5 text-sm text-brand-sage inline-flex items-center gap-1"><Check size={16} /> Saved to your dashboard</p>
                ) : (
                  <div className="mt-5 rounded-xl bg-brand-cream2 p-4 text-sm text-brand-inksoft">
                    <Link to="/signup" className="text-brand-sage font-semibold underline">Create a free account</Link> to save your results and unlock the Silver library.
                  </div>
                )}

                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  <button onClick={restart} className="btn-outline !py-2.5" data-testid="quiz-retake"><RotateCcw size={16} /> Retake</button>
                  <Link to="/packages" className="btn-primary !py-2.5">Explore Membership <ArrowRight size={16} /></Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Layout>
  );
}
